/* Beacon 12 / Zepto v9.646: keep source, crop and saved preview on one revision.
 * This adapter never edits artwork properties or vendor configuration. Hooks
 * are fingerprinted because their callback contracts are version-specific. */
(() => {
  'use strict';
  if (!/^\/products\/the-beacon-tap-handle-12\/?$/.test(location.pathname) || window.GrrypArtworkGuard) return;
  const expected = {
    pplr_readURL: '60c14d60', canvasimagepplr: '4dbe2cfe', cropperpplr: '2e9134b0',
    pplr_hide: '74c65e86', recalluipplr: '1fd77b21', ChangeCanvas: '47f59036',
    recursivecanvasbottom: '72448b44', pplr_canvas_render_final: '5deee7ab',
    pplr_generate_preview: 'f54668f8', create_pplr_product: 'dfbd4349', pplrRemoveViewCache: 'ec718fd3'
  };
  const records = new Map(), fileTickets = new WeakMap(), canvasTickets = new WeakMap();
  const latestViews = new Map(), previewViews = new Map();
  let revision = 0, serial = 0, installed = false, unsupported = false, sourceScope = null, renderScope = null;
  const original = {};
  const hash = text => { let h = 2166136261; for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619); return (h >>> 0).toString(16).padStart(8, '0'); };
  const live = () => [...records.values()].filter(r => r.input.isConnected);
  const currentSource = s => s && records.get(s.record.input) === s.record && (s.cropEpoch === undefined || s.cropEpoch === s.record.cropEpoch);
  const allConfirmed = () => live().every(r => r.sourceLoaded && r.confirmed);
  const currentRender = t => t && t.revision === revision && (t.final || latestViews.get(t.view) === t.serial);
  const ready = () => installed && !unsupported && allConfirmed() && live().every(r => previewViews.get(r.view) === revision);
  const context = () => ({ source: sourceScope, render: renderScope });
  const validContext = c => (!c.source || currentSource(c.source)) && (!c.render || currentRender(c.render));
  function within(c, callback, receiver, args) {
    const prior = context(); sourceScope = c.source; renderScope = c.render;
    try { return callback.apply(receiver, args); }
    finally { sourceScope = prior.source; renderScope = prior.render; }
  }
  function guarded(callback, c = context()) {
    if (typeof callback !== 'function' || (!c.source && !c.render)) return callback;
    return function (...args) { if (validContext(c)) return within(c, callback, this, args); };
  }
  function changed() { revision++; previewViews.clear(); paint(); }
  function paint(message) {
    const pending = live().length > 0 && !ready();
    const form = document.querySelector('.pplr-modal-box form');
    if (!form) return;
    let status = form.querySelector('[data-grryp-artwork-status]');
    if (!status) { status = document.createElement('p'); status.dataset.grrypArtworkStatus = ''; status.setAttribute('role', 'status'); form.append(status); }
    status.textContent = message || (pending ? (unsupported ? 'Artwork preparation could not start. Keep this design open and reload to retry, or contact the shop.' : 'Your artwork is updating. Please wait for the preview to finish before adding it to the cart.') : '');
    status.hidden = !status.textContent;
    form.querySelectorAll('.p_a_t_c').forEach(b => { b.setAttribute('aria-busy', String(pending)); if (pending) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled'); });
  }
  function rejectSave(event) {
    if (event?.target instanceof Element && !event.target.closest('.pplr-modal-box')) return false;
    if (!live().length || ready()) return false;
    event?.preventDefault(); event?.stopImmediatePropagation(); paint(); return true;
  }
  function recordForFrame(frame) { return live().find(r => String(r.frame) === String(frame)); }
  function invalidateViews() { live().forEach(r => original.pplrRemoveViewCache?.(r.frame)); }
  function confirm(record) {
    const crop = record.crop;
    if (!crop || !crop.ready || !currentSource(crop.scope) || !crop.image.isConnected) { record.pendingConfirm = true; paint('Preparing your crop. It will be confirmed when the image is ready.'); return; }
    const jq = window.jQuery(crop.image), instance = jq.data('cropper');
    if (!instance || instance !== crop.instance) { paint('The crop is still loading. Please wait or select the image again.'); return; }
    const data = jq.cropper('getData', true);
    if (!['x', 'y', 'width', 'height'].every(k => Number.isFinite(data[k])) || data.width <= 0 || data.height <= 0) return;
    record.confirmed = true; record.pendingConfirm = false;
    // Flush the vendor's 100 ms crop debounce before destroying the cropper.
    within({source:crop.scope,render:null}, () => window.recalluipplr(data, record.frame, crop.width, crop.height, crop.sourceWidth));
    original.pplr_hide(record.frame, true); paint();
  }
  function install() {
    if (installed || unsupported) return installed;
    if (Object.entries(expected).some(([name, value]) => typeof window[name] !== 'function' || hash(Function.prototype.toString.call(window[name])) !== value)) { unsupported = true; paint(); return false; }
    for (const name of Object.keys(expected)) original[name] = window[name];
    const jq = window.jQuery;
    if (!jq || String(window._CP?.[8]) !== '1' || String(window._CP?.[10]) !== '3') { unsupported = true; return false; }

    // Carry the original file ticket through FileReader and subsequent nested
    // timers/image loads. An obsolete read must never become the newest source.
    const timeout = window.setTimeout, idle = window.requestIdleCallback, load = window.p_loadImage, script = window.loadScript_pplr;
    const Reader = window.FileReader;
    function TicketReader(...args) {
      const reader = new Reader(...args), c = context();
      for (const method of ['readAsDataURL','readAsArrayBuffer','readAsText','readAsBinaryString']) {
        if (typeof reader[method] !== 'function') continue;
        const read = reader[method];
        reader[method] = function (...values) {
          // EXIF readers start outside the render callback. Identify their
          // original File, never whichever file happens to be selected later.
          const record = fileTickets.get(values[0]);
          const owner = c.source || c.render ? c : record ? {source:{record},render:null} : null;
          if (owner) for (const event of ['onload','onloadend','onerror','onabort','onprogress']) this[event] = guarded(this[event], owner);
          return read.apply(this, values);
        };
      }
      return reader;
    }
    Object.setPrototypeOf(TicketReader, Reader); TicketReader.prototype = Reader.prototype;
    window.FileReader = TicketReader;
    window.setTimeout = function (callback, delay, ...args) { return timeout.call(this, guarded(callback), delay, ...args); };
    if (idle) window.requestIdleCallback = function (callback, options) { return idle.call(this, guarded(callback), options); };
    window.p_loadImage = function (...args) {
      const promise = load.apply(this, args), c = context();
      if (!c.source && !c.render) return promise;
      return { then(yes, no) { return promise.then(guarded(yes, c), guarded(no, c)); } };
    };
    window.loadScript_pplr = function (url, callback, ...args) { return script.call(this, url, guarded(callback), ...args); };
    const saveImages = window.saveImages;
    window.saveImages = function (...args) { return saveImages.apply(this, args.map(arg => guarded(arg))); };
    const remove = window.pplr_delete;
    window.pplr_delete = function (frame, ...args) {
      const record = recordForFrame(frame);
      if (record) { records.delete(record.input); changed(); }
      return remove.call(this, frame, ...args);
    };
    if (typeof window.convertHEIC === 'function') {
      const convert = window.convertHEIC;
      window.convertHEIC = function (...args) { const s = sourceScope; return convert.apply(this, args).then(file => { if (s) fileTickets.set(file, s.record); return file; }); };
    }
    window.pplr_readURL = function (file, input, ...args) {
      const record = fileTickets.get(file) || records.get(input);
      if (!record) return original.pplr_readURL.call(this, file, input, ...args);
      const scope = { record };
      if (!currentSource(scope)) return;
      return within({source:scope,render:null}, original.pplr_readURL, this, [file, input, ...args]);
    };
    window.canvasimagepplr = function (...args) {
      if (sourceScope && !currentSource(sourceScope)) return;
      if (sourceScope) sourceScope.record.sourceLoaded = true;
      return original.canvasimagepplr.apply(this, args);
    };
    const cropper = jq.fn.cropper;
    // The Cropper library is lazy. Wrap its initializer when it becomes present.
    function hookCropper() {
      if (!jq.fn.cropper || jq.fn.cropper.grrypArtworkScoped) return;
      const initialize = jq.fn.cropper;
      function scopedCropper(options, ...args) {
        if (options && typeof options === 'object' && sourceScope) {
          options = {...options, crop:guarded(options.crop), ready:guarded(options.ready)};
        }
        return initialize.call(this, options, ...args);
      }
      Object.assign(scopedCropper, initialize); scopedCropper.grrypArtworkScoped = true; jq.fn.cropper = scopedCropper;
    }
    if (cropper) hookCropper();
    const scriptWithScope = window.loadScript_pplr;
    window.loadScript_pplr = function (url, callback, ...args) { return scriptWithScope.call(this, url, function (...values) { hookCropper(); return callback.apply(this, values); }, ...args); };
    window.cropperpplr = function (frame, width, height, ratio, sourceWidth, fresh) {
      const record = sourceScope?.record || recordForFrame(frame);
      if (!record) return original.cropperpplr.apply(this, arguments);
      if (!currentSource({record})) return;
      record.cropEpoch++; record.confirmed = false; changed();
      const scope = {record,cropEpoch:record.cropEpoch};
      const image = document.querySelector('.pplr-modal-img[data-frame="' + frame + '"]');
      if (!image) return;
      const crop = {scope,image,width,height,sourceWidth,ready:false,instance:null}; record.crop = crop;
      jq(image).one('ready.cropper.grrypArtwork', () => {
        if (!currentSource(scope) || !image.complete || !image.naturalWidth) return;
        crop.instance = jq(image).data('cropper'); crop.ready = !!crop.instance;
        if (record.pendingConfirm) confirm(record); else paint();
      });
      return within({source:scope,render:null}, original.cropperpplr, this, [frame,width,height,ratio,sourceWidth,fresh]);
    };
    window.recalluipplr = function (data, frame, ...args) {
      if (sourceScope && !currentSource(sourceScope)) return;
      const record = recordForFrame(frame);
      // Cropper also passes jQuery events containing circular DOM references.
      if (record) { const signature = JSON.stringify(['x','y','width','height','rotate','scaleX','scaleY'].map(key => data[key])); if (signature !== record.cropSignature) { record.cropSignature = signature; changed(); original.pplrRemoveViewCache(frame); } }
      return original.recalluipplr.call(this, data, frame, ...args);
    };
    window.pplr_hide = function (frame, accepted) {
      const record = recordForFrame(frame);
      if (accepted && record) return confirm(record);
      return original.pplr_hide.apply(this, arguments);
    };

    // Capture a render ticket BEFORE its asynchronous background load. Tag the
    // actual canvas when drawing starts; late callbacks cannot relabel it.
    window.ChangeCanvas = function (...args) {
      const view = window.pplr_values[args[0]][92] || window.pplr_values[args[0]][18];
      const ticket = {revision,serial:++serial,view,final:!!window.create_pplr,sourcesVerifiedAtStart:allConfirmed()};
      if (!ticket.final) latestViews.set(view, ticket.serial);
      const previousLoad = window.p_loadImage;
      window.p_loadImage = function (...loadArgs) {
        const promise = previousLoad.apply(this, loadArgs);
        return {then(yes,no) { return promise.then(image => { if (currentRender(ticket)) return within({source:null,render:ticket}, yes, this, [image]); }, no); }};
      };
      try { return original.ChangeCanvas.apply(this, args); }
      finally { window.p_loadImage = previousLoad; }
    };
    window.recursivecanvasbottom = function (...args) {
      const canvas = args[1];
      if (renderScope && !canvasTickets.has(canvas)) canvasTickets.set(canvas, renderScope);
      const ticket = canvasTickets.get(canvas);
      if (ticket && !currentRender(ticket)) return;
      return within({source:null,render:ticket||null}, original.recursivecanvasbottom, this, args);
    };
    window.pplr_canvas_render_final = function (...args) {
      const ticket = canvasTickets.get(args[1]);
      if (live().length && (!currentRender(ticket) || !ticket.sourcesVerifiedAtStart)) return;
      const result = within({source:null,render:ticket||null}, original.pplr_canvas_render_final, this, args);
      // Preserve every final SVG/PDF pass, including two passes on one canvas.
      if (ticket && !ticket.final && currentRender(ticket) && ticket.sourcesVerifiedAtStart) { previewViews.set(ticket.view, revision); paint(); }
      return result;
    };
    window.pplr_generate_preview = function (...args) {
      const canvas = document.querySelector('.pplr_preview_wrapper canvas'), ticket = canvasTickets.get(canvas) || renderScope;
      if (live().length && !currentRender(ticket)) return;
      return within({source:null,render:ticket||null}, original.pplr_generate_preview, this, args);
    };
    window.create_pplr_product = function (event, ...args) {
      if (rejectSave(event)) return false;
      if (event?.target instanceof Element && event.target.closest('.pplr-modal-box') && live().length && !window.create_pplr) invalidateViews();
      return original.create_pplr_product.call(this, event, ...args);
    };
    installed = true; return true;
  }
  window.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file' || !input.matches('.fileupload[data-frame]') || !input.closest('.pplr-modal-box') || !input.files?.[0]) return;
    install();
    const frame = input.dataset.frame, view = window.pplr_values?.[frame]?.[92] || window.pplr_values?.[frame]?.[18];
    const record = {input,frame,view,sourceLoaded:false,confirmed:false,cropEpoch:0,pendingConfirm:false};
    records.set(input, record); fileTickets.set(input.files[0], record); changed();
  }, true);
  // Window capture runs before Zepto's existing document-capture handler,
  // including when an unknown fingerprint prevents installing function hooks.
  window.addEventListener('click', event => {
    if (event.target instanceof Element && event.target.closest('.pplr-modal-box .p_a_t_c')) rejectSave(event);
  }, true);
  window.addEventListener('submit', event => {
    if (event.target instanceof Element && event.target.closest('.pplr-modal-box')) rejectSave(event);
  }, true);
  window.GrrypArtworkGuard = {status:() => ({installed,unsupported,revision,ready:ready(),fields:live().map(r=>({frame:r.frame,sourceLoaded:r.sourceLoaded,confirmed:r.confirmed,cropReady:!!r.crop?.ready,pendingConfirm:r.pendingConfirm,previewRevision:previewViews.get(r.view)}))})};
})();
